import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ArrowRight, MessageCircle, Check, Mail, UserCheck, FileText } from "lucide-react";
import { Link } from "../components/LocaleLink";
import { useLang } from "../context/LanguageContext";
import { Seo } from "../components/Seo";
import { waHref } from "../i18n/translations";

const FLOWS = {
  buy: { page: "buyPage", secondary: "/properties", secondaryKey: "browseAll" },
  owner: { page: "ownerPage", secondary: "/property-management", secondaryKey: "learnMore" },
  invest: { page: "investPage", secondary: "/riviera-real-estate-data-2026", secondaryKey: "readReport" },
};
const ICONS = [Mail, UserCheck, FileText];

export default function FlowThankYou({ flow }) {
  const { t } = useLang();
  const { state } = useLocation();
  const cfg = FLOWS[flow];

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const p = (k) => t(`${cfg.page}.${k}`);
  const th = (k) => t(`flowThanks.${k}`);
  const steps = th(`steps.${flow}`);
  const reads = th(`reads.${flow}`);
  const name = state?.name?.trim();

  return (
    <div data-testid={`${flow}-thanks-page`} className="min-h-[80vh]">
      <Seo title={`${p("successTitle")} | Velmira Living`} description={p("successBody")} noindex />
      <section className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1100px] pt-40 pb-20 md:pb-28">
        <div className="text-center vl-fade-up">
          <div className="mx-auto w-14 h-14 rounded-full border border-primary/50 bg-primary/10 flex items-center justify-center"><Check size={22} className="text-primary" /></div>
          {name ? <p data-testid="thanks-greeting" className="mt-8 font-accent text-xs sm:text-sm uppercase tracking-[0.25em] text-primary">{th("greeting").replace("{name}", name)}</p> : null}
          <h1 data-testid={`${flow}-success-title`} className={`${name ? "mt-4" : "mt-8"} font-headings text-4xl sm:text-5xl leading-[1.1] tracking-tight text-foreground`}>{p("successTitle")}</h1>
          <p className="mt-6 font-body font-light text-lg text-foreground/75 max-w-2xl mx-auto">{p("successBody")}</p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a href={waHref(t("contact.whatsappText"))} target="_blank" rel="nofollow noopener noreferrer" data-testid={`${flow}-success-whatsapp`} className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-4 uppercase tracking-[0.2em] text-xs font-body transition-all duration-300">
              <MessageCircle size={15} /> {p("whatsapp")}
            </a>
            <Link to={cfg.secondary} data-testid={`${flow}-success-browse`} className="inline-flex items-center gap-2 border border-foreground/30 text-foreground hover:border-primary hover:text-primary px-8 py-4 uppercase tracking-[0.2em] text-xs font-body transition-all duration-300">
              {p(cfg.secondaryKey)} <ArrowRight size={15} />
            </Link>
          </div>
        </div>

        <div className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-6 vl-fade-up" style={{ animationDelay: "150ms" }} data-testid="thanks-steps">
          {steps.map((s, i) => {
            const Icon = ICONS[i] || FileText;
            return (
              <div key={s.title} className="border border-border/60 bg-secondary/60 p-7">
                <div className="flex items-center gap-3">
                  <span className="font-accent text-xs tracking-[0.25em] text-primary">{String(i + 1).padStart(2, "0")}</span>
                  <Icon size={16} className="text-primary" />
                </div>
                <h3 className="mt-4 font-headings text-xl text-foreground">{s.title}</h3>
                <p className="mt-2 font-body font-light text-sm text-foreground/70 leading-relaxed">{s.body}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-20 vl-fade-up" style={{ animationDelay: "300ms" }} data-testid="thanks-reads">
          <p className="font-accent text-xs uppercase tracking-[0.25em] text-primary">{th("readTitle")}</p>
          <ul className="mt-6 divide-y divide-border/50 border-y border-border/50">
            {reads.map((r) => (
              <li key={r.to}>
                <Link to={r.to} className="group flex items-center justify-between gap-6 py-5 font-body text-foreground/85 hover:text-primary transition-colors duration-300">
                  <span>{r.label}</span>
                  <ArrowRight size={16} className="shrink-0 text-primary transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
